import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { withStyles } from 'theme/withStyles';
import RawHtml from 'common/RawHtml';
import renderGroups from 'utils/renderGroups';
import pageActions from 'core/page/actions';
import Spinner from 'common/Spinner';
import ErrorMsg from 'common/ErrorMsg';
import dataActions from 'core/data/actions';
import ButtonRowPage from './common/ButtonRowPage';

import rootStyles from './styles/root';

const styles = (theme) => {
  const common = {
    root: rootStyles(theme),
  };
  return {
    ...common,
    forceMobileView: {
      '& fieldset[class*="-group-view"]:not(.active)': {
        display: 'none',
      },
    },
  };
};

const getWrapClass = (name) => {
  switch (name) {
    case 'autoexport':
      return 'autoexport-view';
    case 'beneficiaries':
      return 'beneficiaries-view';
    case 'documents':
      return 'documents-page-view';
    case 'esign':
      return 'esign-view';
    case 'funding':
      return 'funding-view';
    case 'offers':
      return 'offers-view';
    case 'primaryinfo':
      return 'primary-info-view';
    case 'qualify':
      return 'qualify-view';
    case 'secondaryinfo':
      return 'secondary-info-view';
    case 'signerinfo':
      return 'signer-info-view';
    case 'signerstatus':
      return 'signerstatus-view';
    default:
      return 'dynamic-view';
  }
};

const getWithButtons = (name) => {
  switch (name) {
    case 'autoexport':
    case 'funding':
    case 'offers':
    case 'qualify':
    case 'signerstatus':
    case 'prodstart':
    case 'primaryverification':
      return false;
    default:
      return true;
  }
};

const getForceMobileView = (name) => {
  switch (name) {
    case 'funding':
      return true;
    default:
      return false;
  }
};

export const DefaultBare = ({
  backAllowed,
  backButtonLabel,
  changeData,
  classes,
  continueAllowed,
  didStartAutoexport,
  didStartQualify,
  disableButton,
  groups,
  headerHtml,
  isAllValidationPassed,
  isSocureDeviceRiskEnabled,
  loading,
  name,
  path,
  resendEmailTokenMsg,
  saveAndContinueAllowed,
  saveButtonLabel,
  socureApiKey,
  socureEndpointUrl,
  start,
  submitButtonLabel,
}) => {
  useEffect(() => {
    if (
      (path === 'qualify' && name === 'qualify' && !didStartQualify) ||
      (path === 'autoexport' && name === 'autoexport' && !didStartAutoexport)
    ) {
      start();
    }
  }, [didStartAutoexport, didStartQualify, name, path, start]);

  useEffect(() => {
    if (['primaryinfo', 'secondaryinfo'].includes(name) && isSocureDeviceRiskEnabled) {
      const script = document.createElement('script');
      script.src = 'https://js.dvnfo.com/devicer.min.js';
      script.id = 'deviceRisk';
      script.async = true;
      const deviceFPOptions = {
        context: name,
        endpoint: socureEndpointUrl,
        publicKey: socureApiKey,
        userConsent: true,
      };
      script.onload = () => {
        window.devicer.run(deviceFPOptions, (response) => {
          const keyToUse =
            name === 'primaryinfo'
              ? 'appRequest_primary_socureDeviceSessionId'
              : 'appRequest_secondary_socureDeviceSessionId';
          changeData(keyToUse, response.sessionId);
        });
      };
      script.onerror = (e) => {
        window.console.log('devicer error', e);
      };
      document.body.appendChild(script);
    }
    return () => {
      const scriptToRemove = document.querySelector('#deviceRisk');
      if (scriptToRemove) {
        scriptToRemove.parentNode.removeChild(scriptToRemove);
      }
    };
  }, [changeData, isSocureDeviceRiskEnabled, name, socureApiKey, socureEndpointUrl]);

  return (
    <div
      className={classnames(getWrapClass(name), classes.root, {
        [classes.forceMobileView]: getForceMobileView(name),
      })}
    >
      <RawHtml>{headerHtml}</RawHtml>

      {!isAllValidationPassed && <ErrorMsg type="page" />}

      {resendEmailTokenMsg && <RawHtml type="p">{resendEmailTokenMsg}</RawHtml>}

      <div className="row">
        <div className="widget">
          <div className="region-groups">{renderGroups(groups)}</div>
        </div>
      </div>

      {!isAllValidationPassed && <ErrorMsg type="page" />}

      <Spinner {...{ loading, name }} type="page" />

      {getWithButtons(name) && (
        <ButtonRowPage
          {...{
            backAllowed,
            backButtonLabel,
            continueAllowed,
            disableButton,
            loading,
            name,
            saveAndContinueAllowed,
            saveButtonLabel,
            submitButtonLabel,
          }}
        />
      )}
    </div>
  );
};

DefaultBare.propTypes = {
  backAllowed: PropTypes.bool,
  backButtonLabel: PropTypes.string,
  changeData: PropTypes.func.isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  continueAllowed: PropTypes.bool,
  didStartAutoexport: PropTypes.bool.isRequired,
  didStartQualify: PropTypes.bool.isRequired,
  disableButton: PropTypes.bool,
  groups: PropTypes.shape({}),
  headerHtml: PropTypes.string,
  isAllValidationPassed: PropTypes.bool,
  isSocureDeviceRiskEnabled: PropTypes.bool,
  loading: PropTypes.bool,
  name: PropTypes.string,
  path: PropTypes.string,
  resendEmailTokenMsg: PropTypes.string,
  saveAndContinueAllowed: PropTypes.bool,
  saveButtonLabel: PropTypes.string,
  socureApiKey: PropTypes.string,
  socureEndpointUrl: PropTypes.string,
  start: PropTypes.func.isRequired,
  submitButtonLabel: PropTypes.string,
};

DefaultBare.defaultProps = {
  backAllowed: false,
  backButtonLabel: null,
  continueAllowed: true,
  disableButton: false,
  groups: {},
  headerHtml: null,
  isAllValidationPassed: false,
  isSocureDeviceRiskEnabled: false,
  loading: false,
  name: null,
  path: null,
  resendEmailTokenMsg: null,
  saveAndContinueAllowed: false,
  saveButtonLabel: null,
  socureApiKey: null,
  socureEndpointUrl: null,
  submitButtonLabel: null,
};

const getSpinner = (ui, session, path) => {
  switch (path) {
    case 'autoexport':
      return ui.pageLoading && session.meta.configs.autoexport_spinny;
    case 'qualify':
      return ui.pageLoading && session.meta.configs.qualify_spinny;
    default:
      return ui.pageLoading && session.meta.configs.page_spinny;
  }
};

const mapStateToProps = ({ configs, fields, groups, page, resources, session, ui }, { path }) => {
  const isAllPassed = page.errorFields?.length
    ? page.errorFields.map((fl) => fields[fl]?.error).every((v) => v === false || v === undefined)
    : false;

  return {
    backAllowed: page.backAllowed,
    backButtonLabel: page.backButtonLabel,
    continueAllowed: page.continueAllowed,
    didStartAutoexport: ui.didStartAutoexport,
    didStartQualify: ui.didStartQualify,
    groups,
    headerHtml: page.headerHtml,
    isAllValidationPassed: isAllPassed,
    isSocureDeviceRiskEnabled: configs.isSocureDeviceRiskEnabled,
    loading: getSpinner(ui, session, path),
    name: page.name,
    resendEmailTokenMsg: resources.resendEmailTokenMsg,
    saveAndContinueAllowed: page.saveAndContinueAllowed,
    saveButtonLabel: page.saveButtonLabel,
    socureApiKey: configs.socureApiKey,
    socureEndpointUrl: configs.socureEndpointUrl,
    submitButtonLabel: page.submitButtonLabel,
  };
};

const mapDispatchToProps = {
  changeData: dataActions.changeData,
  start: pageActions.start,
};

const DefaultStyled = withStyles(styles)(DefaultBare);

export default connect(mapStateToProps, mapDispatchToProps)(DefaultStyled);
